import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/api/client";
import { useFavorites } from "@/context/FavoritesContext";
import PropertyCard from "@/components/PropertyCard";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Heart, Share2, Trash2, Copy, Check, ExternalLink, GitCompare } from "lucide-react";

export default function FavoritesPage() {
  const { slugs, count, clear } = useFavorites();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareName, setShareName] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const results = [];
      for (const s of slugs) {
        try {
          const { data } = await api.get(`/properties/${s}`);
          results.push(data);
        } catch {
          // Silently skip missing property
        }
      }
      if (!cancelled) { setItems(results); setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [slugs]);

  const createShare = async () => {
    if (slugs.length === 0) return;
    setShareBusy(true);
    try {
      const { data } = await api.post("/shared-favorites", { property_slugs: slugs, name: shareName });
      const url = `${window.location.origin}${data.url}`;
      setShareUrl(url);
      toast.success("Link de partilha criado.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao criar link.");
    } finally {
      setShareBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copiado.");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  return (
    <div data-testid="page-favoritos">
      <SEO title="Os meus favoritos" description="A sua lista privada de imóveis favoritos em casa no porto." />
      <section className="container-cnp pt-14 lg:pt-20 pb-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="tag-chip">Favoritos</span>
            <h1 className="mt-4 font-display text-4xl md:text-5xl text-stone-900">A sua lista de imóveis</h1>
            <p className="mt-4 max-w-2xl text-stone-600">Guardados no seu navegador. Pode partilhar esta lista com quem quiser — família, cônjuge, banco.</p>
          </div>
          {count > 0 && (
            <div className="flex items-center gap-2">
              <Link to="/comparar" data-testid="favoritos-compare-link" className="btn-secondary"><GitCompare className="h-4 w-4" /> Comparar</Link>
              <button onClick={clear} data-testid="favoritos-clear" className="btn-ghost text-red-700"><Trash2 className="h-4 w-4" /> Limpar tudo</button>
            </div>
          )}
        </div>
      </section>

      {count === 0 ? (
        <section className="container-cnp pb-24">
          <div className="card-soft p-12 text-center">
            <Heart className="h-12 w-12 text-stone-300 mx-auto" />
            <h2 className="mt-4 font-display text-2xl text-stone-900">Ainda não tem favoritos.</h2>
            <p className="mt-2 text-stone-600">Toque no coração de qualquer imóvel para guardar aqui.</p>
            <Link to="/imoveis" className="btn-primary mt-6 inline-flex">Explorar imóveis</Link>
          </div>
        </section>
      ) : (
        <>
          <section className="container-cnp pb-6">
            <div className="card-soft p-6">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-xs uppercase tracking-widest text-stone-500">Partilhar esta lista</div>
                  <div className="mt-1 text-sm text-stone-600">Gere um link único. Quem receber vê os imóveis sem precisar de conta.</div>
                </div>
                <input
                  value={shareName}
                  onChange={(e) => setShareName(e.target.value)}
                  placeholder="Nome opcional (ex: Ana e Pedro)"
                  data-testid="favoritos-share-name"
                  className="rounded-full border border-stone-200 bg-white px-4 py-2 text-sm"
                />
                <button onClick={createShare} disabled={shareBusy} data-testid="favoritos-share-create" className="btn-primary">
                  <Share2 className="h-4 w-4" /> {shareBusy ? "A criar..." : "Gerar link"}
                </button>
              </div>
              {shareUrl && (
                <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-stone-50 border border-stone-200">
                  <input readOnly value={shareUrl} data-testid="favoritos-share-url" className="flex-1 bg-transparent text-sm text-stone-800 outline-none" />
                  <button onClick={copyLink} data-testid="favoritos-share-copy" className="btn-ghost text-sm">
                    {copied ? <Check className="h-4 w-4 text-green-700" /> : <Copy className="h-4 w-4" />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                  <a href={shareUrl} target="_blank" rel="noreferrer" className="btn-ghost text-sm"><ExternalLink className="h-4 w-4" /> Abrir</a>
                </div>
              )}
            </div>
          </section>
          <section className="container-cnp pb-24">
            {loading ? <div className="text-stone-500">A carregar...</div> : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((p) => <PropertyCard key={p.id} property={p} />)}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
